
import React, {
	createContext,
	useState,
	useEffect,
	useContext,
} from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { toast } from "@/components/ui/use-toast";

interface SupabaseProfile {
	id: string;
	email: string;
	first_name: string;
	last_name: string;
	role: string;
}

// Add this to your existing types or create a new one
export interface Organization {
	id: string;
	name: string;
	// Add other fields as needed
}

export interface AuthContextValue {
	user: User | null;
	loading: boolean;
	userRole: string;
	isAdmin: boolean;
	isSuperAdmin: boolean;
	signIn: (email: string, password: string) => Promise<{ error?: Error }>;
	signOut: () => Promise<void>;
	signUp: (email: string, password: string) => Promise<void>;
	organizations: Organization[];
	currentOrganization: Organization | null;
	refreshUserData: () => Promise<void>;
	userProfile: SupabaseProfile | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const [userProfile, setUserProfile] = useState<SupabaseProfile | null>(null);
	const [userRole, setUserRole] = useState<string>("user");
	const [organizations, setOrganizations] = useState<Organization[]>([]);
	const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
	const [authInitialized, setAuthInitialized] = useState(false);

	const isAdmin = userRole === "admin";
	const isSuperAdmin = userRole === "super_admin";

	const signIn = async (email: string, password: string) => {
		setLoading(true);
		try {
			const { data, error } = await supabase.auth.signInWithPassword({
				email,
				password,
			});
			if (error) throw error;
			setUser(data.user);
			return { error: undefined };
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message,
				variant: "destructive",
			});
			return { error };
		} finally {
			setLoading(false);
		}
	};

	const signOut = async () => {
		setLoading(true);
		try {
			const { error } = await supabase.auth.signOut();
			if (error) throw error;
			setUser(null);
			setUserProfile(null);
			setUserRole("user");
			setOrganizations([]);
			setCurrentOrganization(null);
			toast({
				title: "Success",
				description: "Signed out successfully",
			});
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message,
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};

	const signUp = async (email: string, password: string) => {
		setLoading(true);
		try {
			const { data, error } = await supabase.auth.signUp({
				email,
				password,
			});
			if (error) throw error;
			setUser(data.user);
			toast({
				title: "Success",
				description: "Signed up successfully. Check your email to verify.",
			});
		} catch (error: any) {
			toast({
				title: "Error",
				description: error.message,
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};

	// Fetch organizations for the current user - extracted to prevent nesting in useEffect
	const fetchOrganizations = async (userId: string) => {
		try {
			// Get all organizations the user belongs to
			const { data: userOrgs, error: userOrgsError } = await supabase
				.from('user_organization')
				.select('organization_uuid')
				.eq('user_uuid', userId);

			if (userOrgsError) throw userOrgsError;
			
			if (userOrgs && userOrgs.length > 0) {
				// Get details for each organization
				const orgIds = userOrgs.map(org => org.organization_uuid);
				const { data: organizations, error: orgsError } = await supabase
					.from('organizations')
					.select('*');
					//.in('id', orgIds);
				
				if (orgsError) throw orgsError;

				console.log("Organizations:", organizations);
				
				if (organizations && organizations.length > 0) {

					setOrganizations(organizations);
					
					// Find the current organization (the one marked as current for this user)
					const { data: userOrg, error: currentOrgError } = await supabase
						.from('user_organization')
						.select('organization_uuid')
						.eq('user_uuid', userId)
						.single();
					
					if (!currentOrgError && userOrg) {
						// Find this organization in the organizations array
						const currentOrg = organizations.find(org => org.id === userOrg.organization_uuid);
						if (currentOrg) {
							setCurrentOrganization(currentOrg);
						} else if (organizations.length > 0) {
							// Fallback to first organization if no current org found
							setCurrentOrganization(organizations[0]);
						}
					}
				}
			}
		} catch (error) {
			console.error("Error fetching organizations:", error);
		}
	};

	// Get user profile data - extracted to prevent nesting in useEffect
	const fetchUserProfile = async (userId: string) => {
		try {
			const { data, error } = await supabase
				.from('profiles')
				.select('*')
				.eq('id', userId)
				.single();
			
			if (error) throw error;
			
			if (data) {
				setUserProfile(data);
				const role = data.role || 'user';
				setUserRole(role);
			}
		} catch (error) {
			console.error("Error fetching user profile:", error);
		} finally {
			setLoading(false);
		}
	};

	// Refresh user data including organizations
	const refreshUserData = async () => {
		if (!user?.id) return;
		
		try {
			// Update user profile data
			await fetchUserProfile(user.id);
			
			// Also refresh organizations
			await fetchOrganizations(user.id);
			
		} catch (error) {
			console.error("Error refreshing user data:", error);
		}
	};

	useEffect(() => {
		// Prevent multiple initialization cycles
		if (authInitialized) return;

		const initAuth = async () => {
			try {
				// First check if there's an existing session
				const { data: sessionData } = await supabase.auth.getSession();
				const initialSession = sessionData?.session;

				if (initialSession?.user) {
					setUser(initialSession.user);
					await fetchUserProfile(initialSession.user.id);
					await fetchOrganizations(initialSession.user.id);
				} else {
					setLoading(false);
				}
				
				// Set up the auth state change listener after initial check
				const { data: authListener } = supabase.auth.onAuthStateChange(
					async (event, session) => {
						// Simple sync state update first - don't trigger any other calls here
						setUser(session?.user || null);
						
						// Use setTimeout to prevent any recursive auth listener pattern
						if (session?.user) {
							setTimeout(() => {
								fetchUserProfile(session.user.id);
								fetchOrganizations(session.user.id);
							}, 0);
						} else {
							setUserProfile(null);
							setUserRole("user");
							setOrganizations([]);
							setCurrentOrganization(null);
							setLoading(false);
						}
					}
				);
				
				setAuthInitialized(true);
				
				return () => {
					authListener.subscription.unsubscribe();
				};
			} catch (error) {
				console.error("Auth initialization error:", error);
				setLoading(false);
			}
		};

		initAuth();
	}, [authInitialized]);
	
	const authValue = {
		user,
		loading,
		userRole,
		isAdmin,
		isSuperAdmin,
		signIn,
		signOut,
		signUp,
		organizations,
		currentOrganization,
		refreshUserData,
		userProfile
	};
	
	return (
		<AuthContext.Provider value={authValue}>
			{children}
		</AuthContext.Provider>
	);
};
